import {
  PEOPLE_CATEGORIES,
  PEOPLE_CHECKS,
  type PeopleCategoryId,
} from "~/lib/siteglue/agent-readiness/people-registry";
import { croBarsForReport, isCroV5 } from "~/lib/siteglue/agent-readiness/cro-v5";
import { isPeopleReadyV2, peopleReadyChecks, scorePeopleReady } from "~/lib/siteglue/agent-readiness/people-score";
import type { CheckResult, ReportSnapshot } from "~/lib/siteglue/agent-readiness/types";

export type ResultsAudience = "people" | "agents";

export type BenchmarkGroup = {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  checkIds: readonly string[];
};

export type CategoryBar = {
  id: string;
  label: string;
  checkIds: readonly string[];
};

export type ScoredGroup = BenchmarkGroup & {
  checks: CheckResult[];
  score: number | null;
};

export type ScoredCategoryBar = CategoryBar & {
  score: number | null;
};

const AGENT_GROUPS: BenchmarkGroup[] = [
  {
    id: "found_mcp",
    title: "Found MCP server?",
    checkIds: ["mcp_server_card"],
  },
  {
    id: "identified_self",
    title: "Identified self?",
    checkIds: ["jsonld_organization"],
  },
  {
    id: "discover_trust",
    title: "Discovery & Trust",
    subtitle: "Can agents find and trust your website?",
    description: "Agents should be able to discover the site and know they are allowed to read it.",
    checkIds: ["robots_txt", "xml_sitemap", "link_headers", "dns_aid"],
  },
  {
    id: "welcome",
    title: "Agent Access",
    subtitle: "Does your website welcome and provide information to AI agents?",
    description: "Clear crawler rules and readable content tell agents they are welcome.",
    checkIds: [
      "ai_crawler_rules",
      "content_signals",
      "web_bot_auth",
      "llms_txt",
      "markdown_negotiation",
    ],
  },
  {
    id: "understand",
    title: "Business Understanding",
    subtitle: "Can agents understand your services, locations, policies, and contact details?",
    description: "Structured facts help an agent introduce the business without guessing.",
    checkIds: [
      "semantic_html",
      "machine_readable_main",
      "jsonld_services",
      "jsonld_location_hours",
      "jsonld_contact",
      "jsonld_faq_policy",
    ],
  },
  {
    id: "take_action",
    title: "Actions & Transactions",
    subtitle: "Can agents request information, book appointments, or perform supported tasks?",
    description: "Published tools and catalogs let an agent take a supported next step.",
    checkIds: [
      "api_catalog",
      "agent_skills",
      "a2a_agent_card",
      "x402",
      "mpp",
      "ucp",
      "acp",
    ],
  },
  {
    id: "authenticate",
    title: "Authentication & Permissions",
    subtitle: "Can agents securely authenticate or perform authorized actions?",
    description: "Documented auth tells an agent how to act without impersonating a person.",
    checkIds: ["oauth_discovery", "oauth_protected_resource", "auth_md", "ard"],
  },
  {
    id: "operate",
    title: "Headless & Web Interaction",
    subtitle: "Can an agent interact with your business programmatically?",
    description: "MCP, APIs, and WebMCP are different ways an agent can act without scraping the page.",
    checkIds: ["webmcp"],
  },
];

const PEOPLE_GROUPS: BenchmarkGroup[] = [
  {
    id: "questions",
    title: "Are people able to get questions answered quickly?",
    checkIds: [
      "scheduling_or_faq",
      "primary_headline",
      "offering_described",
      "audience_or_area",
      "services_listed",
      "reviews_present",
      "credentials_or_team",
    ],
  },
  {
    id: "contact_ways",
    title: "Do you offer multiple ways for people to contact your business?",
    checkIds: [
      "phone_or_hours",
      "email_or_sms",
      "form_or_alternate",
      "cta_destination",
      "privacy_link",
      "viewport_meta",
      "mobile_nav",
      "core_web_vitals",
    ],
  },
  {
    id: "book_or_message",
    title: "Are people able to book appointments or leave a message?",
    checkIds: [
      "contact_or_booking",
      "cta_language",
      "above_fold_cta",
      "field_count",
      "form_labels",
      "form_errors",
    ],
  },
  {
    id: "after_hours",
    title: "Are people able to get after hours support?",
    checkIds: ["live_assistance", "live_channels_not_tested"],
  },
];

export const AGENT_CATEGORIES: {
  id: string;
  label: string;
  question: string;
  description: string;
  checkIds: readonly string[];
}[] = [
  {
    id: "agent.discovery",
    label: "Discovery & Trust",
    question: "Can agents find and trust your website?",
    description: "Agents should be able to discover the site and know they are allowed to read it.",
    checkIds: ["robots_txt", "xml_sitemap", "link_headers", "dns_aid"],
  },
  {
    id: "agent.access",
    label: "Agent Access",
    question: "Does your website welcome and provide information to AI agents?",
    description: "Clear crawler rules and readable content tell agents they are welcome.",
    checkIds: ["ai_crawler_rules", "content_signals", "web_bot_auth", "llms_txt", "markdown_negotiation"],
  },
  {
    id: "agent.understanding",
    label: "Business Understanding",
    question: "Can agents understand your services, locations, policies, and contact details?",
    description: "Structured facts help an agent introduce the business without guessing.",
    checkIds: [
      "semantic_html",
      "machine_readable_main",
      "jsonld_organization",
      "jsonld_services",
      "jsonld_location_hours",
      "jsonld_contact",
      "jsonld_faq_policy",
    ],
  },
  {
    id: "agent.actions",
    label: "Actions & Transactions",
    question: "Can agents request information, book appointments, or perform supported tasks?",
    description: "Published tools and catalogs let an agent take a supported next step. MCP, API catalogs, and WebMCP are different paths.",
    checkIds: ["api_catalog", "mcp_server_card", "agent_skills", "a2a_agent_card", "x402", "mpp", "ucp", "acp"],
  },
  {
    id: "agent.auth",
    label: "Authentication & Permissions",
    question: "Can agents securely authenticate or perform authorized actions?",
    description: "Documented auth tells an agent how to act without impersonating a person.",
    checkIds: ["oauth_discovery", "oauth_protected_resource", "auth_md", "ard"],
  },
  {
    id: "agent.headless",
    label: "Headless & Web Interaction",
    question: "Can an agent interact with your business programmatically?",
    description: "WebMCP, APIs, and machine-readable interfaces let an agent act without scraping the page.",
    checkIds: ["webmcp"],
  },
];

export const AGENT_CATEGORY_BARS: CategoryBar[] = AGENT_CATEGORIES.map((category) => ({
  id: category.id,
  label: category.label,
  checkIds: category.checkIds,
}));

export const PEOPLE_V2_CATEGORY_BARS: CategoryBar[] = PEOPLE_CATEGORIES.map((category) => ({
  id: category.id,
  label: category.label,
  checkIds: PEOPLE_CHECKS.filter((check) => check.categoryId === category.id).map((check) => check.id),
}));

export const PEOPLE_V2_GROUPS: BenchmarkGroup[] = PEOPLE_CATEGORIES.map((category) => ({
  id: category.id,
  title: category.label,
  subtitle: category.question,
  description: category.description,
  checkIds: PEOPLE_CHECKS.filter((check) => check.categoryId === category.id).map((check) => check.id),
}));

export const SITEGLUE_WALK_BENCHMARKS: CategoryBar[] = [
  { id: "found_mcp", label: "Found MCP server", checkIds: ["mcp_server_card"] },
  { id: "identified_self", label: "Identified self", checkIds: ["jsonld_organization"] },
];

export const PEOPLE_CATEGORY_BARS: CategoryBar[] = [
  {
    id: "discovery",
    label: "Discovery",
    checkIds: [
      "primary_headline",
      "offering_described",
      "audience_or_area",
      "services_listed",
      "reviews_present",
      "credentials_or_team",
    ],
  },
  {
    id: "intent",
    label: "Intent",
    checkIds: [
      "cta_language",
      "cta_destination",
      "above_fold_cta",
      "privacy_link",
      "viewport_meta",
      "mobile_nav",
    ],
  },
  {
    id: "actions",
    label: "Actions",
    checkIds: [
      "contact_or_booking",
      "form_or_alternate",
      "field_count",
      "form_labels",
      "form_errors",
      "phone_or_hours",
      "live_assistance",
      "email_or_sms",
      "scheduling_or_faq",
      "live_channels_not_tested",
      "core_web_vitals",
    ],
  },
];

function countsTowardScore(check: Pick<CheckResult, "status" | "weight">): boolean {
  if (check.weight <= 0) return false;
  if (check.status === "not_applicable" || check.status === "not_tested") return false;
  return true;
}

export function scoreFromCheckIds(
  checks: Pick<CheckResult, "id" | "status" | "weight">[],
  checkIds: readonly string[],
): number | null {
  const idSet = new Set(checkIds);
  const applicable = checks.filter((check) => idSet.has(check.id) && countsTowardScore(check));
  const weight = applicable.reduce((sum, check) => sum + check.weight, 0);
  if (weight === 0) return null;
  const passed = applicable
    .filter((check) => check.status === "passed")
    .reduce((sum, check) => sum + check.weight, 0);
  return Math.round((passed / weight) * 100);
}

export function letterGrade(score: number | null): string {
  if (score == null) return "—";
  if (score >= 90) return "A";
  if (score >= 80) return "B";
  if (score >= 70) return "C";
  if (score >= 60) return "D";
  return "F";
}

export function scoreSummary(audience: ResultsAudience, score: number | null): string {
  if (score == null) {
    return audience === "people"
      ? "People-ready checks were not scored for this scan."
      : "AI agent checks were not scored for this scan.";
  }
  if (audience === "people") {
    if (score >= 70) return "People can find what you do and how to reach you.";
    if (score >= 40) {
      return "People can see the business, but some contact or booking paths are incomplete.";
    }
    return "People still have trouble finding answers or taking the next step.";
  }
  if (score >= 70) return "Agents can discover this site and read enough to introduce you.";
  if (score >= 40) return "Agents can find the site, but they still lack a clear way to act.";
  return "Agents cannot reliably find, read, or act on this site.";
}

export function channelScoreSummary(score: number | null): string {
  if (score == null) return "Channel-ready checks did not have enough conclusive evidence.";
  if (score >= 70) return "People and their agents can message selected channels and get an AI Employee response.";
  if (score >= 40) return "Some selected channels can reply; others still need an AI Employee.";
  return "Selected channels do not yet return an AI Employee response.";
}

export function scoreTone(score: number | null): "ready" | "partly" | "not" | "empty" {
  if (score == null) return "empty";
  if (score >= 70) return "ready";
  if (score >= 40) return "partly";
  return "not";
}

function groupChecks(checks: CheckResult[], groups: BenchmarkGroup[]): ScoredGroup[] {
  const used = new Set<string>();
  const scored = groups.map((group) => {
    const matched = checks.filter((check) => group.checkIds.includes(check.id));
    for (const check of matched) used.add(check.id);
    return {
      ...group,
      checks: matched,
      score: scoreFromCheckIds(matched, group.checkIds),
    };
  });
  const leftover = checks.filter((check) => !used.has(check.id));
  if (leftover.length > 0) {
    scored.push({
      id: "other",
      title: "Other benchmarks",
      checkIds: leftover.map((check) => check.id),
      checks: leftover,
      score: scoreFromCheckIds(leftover, leftover.map((check) => check.id)),
    });
  }
  return scored.filter((group) => group.checks.length > 0);
}

export function peopleBenchmarkGroups(checks: CheckResult[]): ScoredGroup[] {
  return groupChecks(checks, PEOPLE_GROUPS);
}

export function peopleV2BenchmarkGroups(checks: CheckResult[]): ScoredGroup[] {
  const people = peopleReadyChecks(checks);
  const scored = scorePeopleReady(people);
  return PEOPLE_V2_GROUPS.map((group) => ({
    ...group,
    checks: people.filter((check) => group.checkIds.includes(check.id)),
    score: scored.categories.find((category) => category.id === group.id)?.score ?? null,
  })).filter((group) => group.checks.length > 0);
}

export function peopleGroupsForReport(report: ReportSnapshot | null): ScoredGroup[] {
  const checks = report?.checks ?? [];
  if (isPeopleReadyV2(report)) return peopleV2BenchmarkGroups(checks);
  return peopleBenchmarkGroups(checks);
}

export function peopleBarsForReport(report: ReportSnapshot | null): ScoredCategoryBar[] {
  if (isCroV5(report)) {
    return croBarsForReport(report);
  }
  if (isPeopleReadyV2(report)) {
    const scored = scorePeopleReady(report?.checks ?? []);
    return scored.categories.map((category) => ({
      id: category.id as PeopleCategoryId,
      label: PEOPLE_CATEGORIES.find((item) => item.id === category.id)?.label ?? category.label,
      checkIds: PEOPLE_V2_CATEGORY_BARS.find((bar) => bar.id === category.id)?.checkIds ?? [],
      score: category.score,
    }));
  }
  return scoreCategoryBars(report?.checks ?? [], PEOPLE_CATEGORY_BARS);
}

export function agentBenchmarkGroups(checks: CheckResult[]): ScoredGroup[] {
  return groupChecks(checks, AGENT_GROUPS);
}

export const PEOPLE_GROUP_TITLES = PEOPLE_GROUPS.map((group) => group.title);
export const AGENT_GROUP_TITLES = AGENT_GROUPS.map((group) => group.title);

export function scoreCategoryBars(
  checks: Pick<CheckResult, "id" | "status" | "weight">[],
  bars: CategoryBar[],
): ScoredCategoryBar[] {
  return bars.map((bar) => ({
    ...bar,
    score: scoreFromCheckIds(checks, bar.checkIds),
  }));
}

export function checkTag(check: Pick<CheckResult, "id" | "title">): string {
  if (check.id.startsWith("CH-")) return check.title;
  if (check.id === "mcp_server_card") return "found-mcp-server";
  if (check.id === "jsonld_organization") return "identified-self";
  const people = PEOPLE_CHECKS.find((item) => item.id === check.id);
  if (people) return people.title;
  return check.id.replace(/^jsonld_/, "").replace(/_/g, "-");
}
